import { useEffect, useState } from "react";
import { supabase } from "./lib/supabaseClient";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, CartesianGrid, PieChart, Pie, Cell
} from "recharts";

const COLORS = ["#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#06b6d4"];

export default function QRDetails({ qr }) {
  const id = qr?.id;
  const [data, setData] = useState({
    total: 0, unique: 0, repeat: 0,
    timeline: [], hourly: [], countries: [], cities: [],
    devices: [], browsers: [], recentScans: [],
    abResults: null, conversionRate: null
  });
  const [loading, setLoading] = useState(true);
  const [activeMap, setActiveMap] = useState("countries");
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    async function fetchAnalytics() {
      if (!id) { setLoading(false); return; }
      try {
        setLoading(true);

        const [logsRes, leadsRes] = await Promise.all([
          supabase.from("qr_logs")
            .select("scanned_at, country, city, device_type, browser_type, ip_address, hour_of_day, ab_variant")
            .eq("qr_id", id)
            .order("scanned_at", { ascending: false }),
          supabase.from("qr_leads")
            .select("id", { count: "exact", head: true })
            .eq("qr_id", id)
        ]);

        const logs = logsRes.data || [];
        const leadCount = leadsRes.count || 0;
        const total = logs.length;

        // Unique vs Repeat by IP
        const ipMap = {};
        logs.forEach(log => {
          const ip = log.ip_address || "unknown";
          ipMap[ip] = (ipMap[ip] || 0) + 1;
        });
        const unique = Object.keys(ipMap).length;
        const repeat = total - unique;

        // Conversion rate (only for leads QRs)
        const conversionRate = total > 0 && qr.qr_mode === "leads"
          ? ((leadCount / total) * 100).toFixed(1)
          : null;

        // Timeline by date
        const timelineMap = {};
        logs.forEach(log => {
          const date = new Date(log.scanned_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
          timelineMap[date] = (timelineMap[date] || 0) + 1;
        });
        const timeline = Object.entries(timelineMap).reverse().map(([date, count]) => ({ date, count }));

        // Hourly heatmap
        const hourlyMap = Array(24).fill(0);
        logs.forEach(log => {
          const h = log.hour_of_day ?? new Date(log.scanned_at).getHours();
          hourlyMap[h]++;
        });
        const hourly = hourlyMap.map((count, h) => ({
          hour: h === 0 ? "12am" : h < 12 ? `${h}am` : h === 12 ? "12pm" : `${h-12}pm`,
          count
        }));

        // Countries
        const countryMap = {};
        logs.forEach(log => { const c = log.country || "Unknown"; countryMap[c] = (countryMap[c] || 0) + 1; });
        const countries = Object.entries(countryMap).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 10);

        // Cities
        const cityMap = {};
        logs.forEach(log => { const c = log.city || "Unknown"; cityMap[c] = (cityMap[c] || 0) + 1; });
        const cities = Object.entries(cityMap).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 10);

        // Devices
        const deviceMap = {};
        logs.forEach(log => { const d = log.device_type || "Unknown"; deviceMap[d] = (deviceMap[d] || 0) + 1; });
        const devices = Object.entries(deviceMap).map(([name, count]) => ({ name, count }));

        // Browsers
        const browserMap = {};
        logs.forEach(log => { const b = log.browser_type || "Unknown"; browserMap[b] = (browserMap[b] || 0) + 1; });
        const browsers = Object.entries(browserMap).map(([name, count]) => ({ name, count }));

        // A/B Results
        let abResults = null;
        if (qr.ab_testing) {
          const aCount = logs.filter(l => l.ab_variant === "A").length;
          const bCount = logs.filter(l => l.ab_variant === "B").length;
          if (aCount + bCount > 0) {
            abResults = {
              a: { count: aCount, url: qr.ab_url_a, pct: ((aCount / (aCount + bCount)) * 100).toFixed(0) },
              b: { count: bCount, url: qr.ab_url_b, pct: ((bCount / (aCount + bCount)) * 100).toFixed(0) }
            };
          }
        }

        // Recent scans
        const recentScans = logs.slice(0, 20).map(log => ({
          time: new Date(log.scanned_at).toLocaleString(),
          country: log.country || "Unknown",
          city: log.city || "Unknown",
          device: log.device_type || "Unknown",
          browser: log.browser_type || "Unknown",
          ip: log.ip_address || "Unknown",
        }));

        setData({ total, unique, repeat, conversionRate, timeline, hourly, countries, cities, devices, browsers, recentScans, abResults });
      } catch (err) {
        console.error("FETCH FAILED:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchAnalytics();
  }, [id]);

  if (!id) return <div className="p-20 text-center text-red-500 font-black">Invalid QR ID</div>;
  if (loading) return (
    <div className="p-20 text-center">
      <div className="animate-spin inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mb-4" />
      <p className="text-gray-500 font-black uppercase tracking-widest text-[10px]">Processing Analytics...</p>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-500">

      {/* STAT CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Scans" value={data.total} color="blue" />
        <StatCard label="Unique Visitors" value={data.unique} color="green" />
        <StatCard label="Repeat Scans" value={data.repeat} color="purple" />
        {data.conversionRate !== null
          ? <StatCard label="Conversion Rate" value={`${data.conversionRate}%`} color="yellow" />
          : <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
              <p className="text-gray-400 text-[10px] uppercase font-black tracking-widest mb-1">Destination</p>
              <p className="text-white text-[10px] font-medium truncate mt-2">{qr?.destination_url || "N/A"}</p>
            </div>
        }
      </div>

      {/* TAB NAV */}
      <div className="flex gap-2 border-b border-white/10 pb-0">
        {["overview", "geography", "audience", "heatmap", "recent"].concat(data.abResults ? ["a/b results"] : []).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-[10px] font-black uppercase tracking-widest transition-all border-b-2 -mb-px ${activeTab === tab ? "border-blue-500 text-white" : "border-transparent text-gray-500 hover:text-gray-300"}`}>
            {tab}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          <div className="bg-black/40 p-6 rounded-3xl border border-white/10">
            <h3 className="text-sm font-black uppercase tracking-widest mb-6 text-white">Scans Over Time</h3>
            {data.timeline.length === 0 ? <p className="text-gray-500 text-sm">No data available</p> : (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.timeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={9} tickLine={false} axisLine={false} />
                    <YAxis stroke="#64748b" fontSize={9} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #ffffff20", borderRadius: 12 }} />
                    <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={3} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Unique vs Repeat */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-black/40 p-6 rounded-3xl border border-white/10">
              <h3 className="text-sm font-black uppercase tracking-widest mb-6 text-white">Unique vs Repeat</h3>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={[{ name: "Unique", value: data.unique }, { name: "Repeat", value: data.repeat }]}
                      dataKey="value" cx="50%" cy="50%" outerRadius={60}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={9}>
                      <Cell fill="#10b981" />
                      <Cell fill="#8b5cf6" />
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #ffffff20", borderRadius: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-around mt-4">
                <div className="text-center">
                  <div className="flex items-center gap-2 justify-center mb-1"><div className="w-2 h-2 rounded-full bg-green-500" /><span className="text-[10px] text-gray-500 uppercase font-black">Unique</span></div>
                  <p className="text-2xl font-black text-white">{data.unique}</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center gap-2 justify-center mb-1"><div className="w-2 h-2 rounded-full bg-purple-500" /><span className="text-[10px] text-gray-500 uppercase font-black">Repeat</span></div>
                  <p className="text-2xl font-black text-white">{data.repeat}</p>
                </div>
              </div>
            </div>

            {/* Conversion (only for leads) */}
            {data.conversionRate !== null && (
              <div className="bg-black/40 p-6 rounded-3xl border border-white/10">
                <h3 className="text-sm font-black uppercase tracking-widest mb-4 text-white">Conversion Rate</h3>
                <div className="flex items-center justify-center h-40">
                  <div className="text-center">
                    <p className="text-6xl font-black text-yellow-400">{data.conversionRate}%</p>
                    <p className="text-[10px] text-gray-500 uppercase font-black tracking-widest mt-2">Scans → Lead Forms Submitted</p>
                    <p className="text-[9px] text-gray-600 mt-1">{data.total} scans · {Math.round(data.total * data.conversionRate / 100)} leads</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* GEOGRAPHY TAB */}
      {activeTab === "geography" && (
        <div className="space-y-6">
          <div className="bg-black/40 p-6 rounded-3xl border border-white/10">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-sm font-black uppercase tracking-widest text-white">Geographic Breakdown</h3>
              <div className="flex bg-white/5 rounded-xl p-1 gap-1">
                {["countries", "cities", "chart"].map(tab => (
                  <button key={tab} onClick={() => setActiveMap(tab)}
                    className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all ${activeMap === tab ? "bg-white text-black" : "text-gray-500"}`}>
                    {tab}
                  </button>
                ))}
              </div>
            </div>
            {activeMap === "countries" && (
              <div className="space-y-2">
                {data.countries.length === 0 ? <p className="text-gray-500 text-sm">No data</p> : data.countries.map((c, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-[10px] text-gray-500 w-4">{i + 1}</span>
                    <div className="flex-1">
                      <div className="flex justify-between mb-1">
                        <span className="text-[11px] font-black text-white">{c.name}</span>
                        <span className="text-[10px] text-gray-400">{c.count} · {((c.count / data.total) * 100).toFixed(0)}%</span>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(c.count / data.total) * 100}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {activeMap === "cities" && (
              <div className="space-y-2">
                {data.cities.length === 0 ? <p className="text-gray-500 text-sm">No data</p> : data.cities.map((c, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-[10px] text-gray-500 w-4">{i + 1}</span>
                    <div className="flex-1">
                      <div className="flex justify-between mb-1">
                        <span className="text-[11px] font-black text-white">{c.name}</span>
                        <span className="text-[10px] text-gray-400">{c.count} · {((c.count / data.total) * 100).toFixed(0)}%</span>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-500 rounded-full" style={{ width: `${(c.count / data.total) * 100}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {activeMap === "chart" && (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.countries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={9} />
                    <YAxis stroke="#64748b" fontSize={9} />
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #ffffff20", borderRadius: 12 }} />
                    <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AUDIENCE TAB */}
      {activeTab === "audience" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-black/40 p-6 rounded-3xl border border-white/10">
            <h3 className="text-sm font-black uppercase tracking-widest mb-6 text-white">Device Types</h3>
            {data.devices.length === 0 ? <p className="text-gray-500 text-sm">No data</p> : (
              <>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.devices} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={60}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={9}>
                        {data.devices.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #ffffff20", borderRadius: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2 mt-4">
                  {data.devices.map((d, i) => (
                    <div key={i} className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="text-[11px] text-white font-black">{d.name}</span>
                      </div>
                      <span className="text-[10px] text-gray-400">{d.count} · {((d.count / data.total) * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="bg-black/40 p-6 rounded-3xl border border-white/10">
            <h3 className="text-sm font-black uppercase tracking-widest mb-6 text-white">Browsers</h3>
            {data.browsers.length === 0 ? <p className="text-gray-500 text-sm">No data</p> : (
              <div className="space-y-3">
                {data.browsers.map((b, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex justify-between mb-1">
                        <span className="text-[11px] font-black text-white">{b.name}</span>
                        <span className="text-[10px] text-gray-400">{b.count} · {((b.count / data.total) * 100).toFixed(0)}%</span>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(b.count / data.total) * 100}%`, background: COLORS[i % COLORS.length] }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* HEATMAP TAB */}
      {activeTab === "heatmap" && (
        <div className="bg-black/40 p-6 rounded-3xl border border-white/10">
          <h3 className="text-sm font-black uppercase tracking-widest mb-2 text-white">Scan Heatmap by Hour</h3>
          <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-6">Peak scanning hours</p>
          {data.hourly.every(h => h.count === 0) ? (
            <p className="text-gray-500 text-sm">No hourly data available yet. Scans need `hour_of_day` column — run the SQL migration first.</p>
          ) : (
            <>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.hourly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                    <XAxis dataKey="hour" stroke="#64748b" fontSize={8} tickLine={false} axisLine={false} interval={2} />
                    <YAxis stroke="#64748b" fontSize={9} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #ffffff20", borderRadius: 12 }} />
                    <Bar dataKey="count" radius={[3, 3, 0, 0]}
                      fill="#3b82f6"
                      label={false}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Peak hour callout */}
              {(() => {
                const peak = data.hourly.reduce((a, b) => a.count > b.count ? a : b, { count: 0, hour: "" });
                return peak.count > 0 ? (
                  <div className="mt-4 bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4 flex items-center gap-4">
                    <span className="text-2xl">⏰</span>
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase font-black">Peak Hour</p>
                      <p className="text-white font-black">{peak.hour} — {peak.count} scans</p>
                    </div>
                  </div>
                ) : null;
              })()}
            </>
          )}
        </div>
      )}

      {/* RECENT SCANS TAB */}
      {activeTab === "recent" && (
        <div className="bg-black/40 p-6 rounded-3xl border border-white/10">
          <h3 className="text-sm font-black uppercase tracking-widest mb-6 text-white">Recent Scans</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/10">
                  {["Time", "Country", "City", "Device", "Browser", "IP Address"].map(h => (
                    <th key={h} className="pb-3 text-[9px] font-black uppercase text-gray-500 tracking-widest pr-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {data.recentScans.length === 0 ? (
                  <tr><td colSpan={6} className="py-8 text-center text-gray-500 text-sm">No scans yet</td></tr>
                ) : data.recentScans.map((scan, i) => (
                  <tr key={i} className="hover:bg-white/5 transition-colors">
                    <td className="py-3 text-[10px] text-gray-400 pr-4 whitespace-nowrap">{scan.time}</td>
                    <td className="py-3 text-[10px] text-white font-black pr-4">{scan.country}</td>
                    <td className="py-3 text-[10px] text-gray-300 pr-4">{scan.city}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-[9px] font-black px-2 py-1 rounded-lg ${scan.device === "Mobile" ? "bg-blue-500/20 text-blue-400" : "bg-purple-500/20 text-purple-400"}`}>
                        {scan.device}
                      </span>
                    </td>
                    <td className="py-3 text-[10px] text-gray-300 pr-4">{scan.browser}</td>
                    <td className="py-3 text-[10px] text-gray-500 font-mono">{scan.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* A/B RESULTS TAB */}
      {activeTab === "a/b results" && data.abResults && (
        <div className="space-y-6">
          <div className="bg-black/40 p-6 rounded-3xl border border-white/10">
            <h3 className="text-sm font-black uppercase tracking-widest mb-6 text-white">A/B Test Results</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {["a", "b"].map(variant => {
                const v = data.abResults[variant];
                const isWinner = data.abResults.a.count !== data.abResults.b.count &&
                  ((variant === "a" && data.abResults.a.count > data.abResults.b.count) ||
                   (variant === "b" && data.abResults.b.count > data.abResults.a.count));
                return (
                  <div key={variant} className={`p-6 rounded-2xl border relative ${isWinner ? "border-green-500/40 bg-green-500/5" : "border-white/10 bg-white/5"}`}>
                    {isWinner && (
                      <div className="absolute top-4 right-4 bg-green-500 text-white text-[9px] font-black uppercase px-2 py-1 rounded-full">
                        Winner 🏆
                      </div>
                    )}
                    <p className="text-2xl font-black text-white mb-1">Version {variant.toUpperCase()}</p>
                    <p className="text-[10px] text-gray-500 truncate mb-4">{v.url}</p>
                    <p className="text-4xl font-black text-white">{v.count}</p>
                    <p className="text-[10px] text-gray-500 uppercase font-black">Scans · {v.pct}%</p>
                    <div className="mt-4 h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${isWinner ? "bg-green-500" : "bg-blue-500"}`} style={{ width: `${v.pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
              <p className="text-[10px] text-gray-500 uppercase font-black tracking-widest">Total A/B Tracked Scans</p>
              <p className="text-2xl font-black text-white mt-1">{data.abResults.a.count + data.abResults.b.count}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color = "blue" }) {
  const colorMap = {
    blue: "text-blue-400", green: "text-green-400",
    purple: "text-purple-400", yellow: "text-yellow-400"
  };
  return (
    <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
      <p className="text-gray-400 text-[10px] uppercase font-black tracking-widest mb-1">{label}</p>
      <h2 className={`text-3xl font-black ${colorMap[color]}`}>{value}</h2>
    </div>
  );
}